/**
 * This page is loaded by Nextjs:
 *  - on the server, when data-fetching methods throw or reject
 *  - on the client, when `getInitialProps` throws or rejects
 *  - on the client, when a React lifecycle method throws or rejects, and it's
 *    caught by the built-in Nextjs error boundary
 *
 * See:
 *  - https://nextjs.org/docs/basic-features/data-fetching/overview
 *  - https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
 *  - https://reactjs.org/docs/error-boundaries.html
 */

import { Box, Divider, Typography } from '@mui/material';

import NextErrorComponent from 'next/error';
import React from 'react';
import theme from 'src/themes';
import useTranslation from 'next-translate/useTranslation';

const CustomErrorComponent = (props) => {
  const { statusCode } = props;
  const { t } = useTranslation();

  const ErrorMessage = (errorCode: number) => {
    switch (errorCode) {
      case 404:
        return t('common:errorPages.notFoundErrorText');
      case 500:
        return t('common:errorPages.internalServerErrorText');
      default:
        return t('common:errorPages.genericErrorText');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing(1),
        }}
      >
        <Typography variant="h4">{statusCode}</Typography>
        <Divider
          orientation="vertical"
          sx={{
            color: theme.palette.common.black,
            margin: `0 ${theme.spacing(3)}`,
          }}
        />
        <Typography variant="body1">{ErrorMessage(statusCode)}</Typography>
      </Box>
    </Box>
  );
};

CustomErrorComponent.getInitialProps = async (contextData) => {
  // This will contain the status code of the response
  return NextErrorComponent.getInitialProps(contextData);
};

export default CustomErrorComponent;
